type ToolEntry = string | { name: string; description?: string };

type CopyFn = (en: string, de: string, hr: string) => string;

type PlanRequiredToolsListProps = {
	tools: ToolEntry[];
	className?: string;
	editable?: boolean;
	isEditing?: boolean;
	onChange?: (next: ToolEntry[]) => void;
};

export default function PlanRequiredToolsList({
	tools,
	className = '',
	editable = false,
	isEditing = false,
	onChange
}: PlanRequiredToolsListProps) {
	const first = tools[0];
	const isStringList = typeof first === 'string';

	if (editable && isEditing && onChange) {
		if (isStringList || tools.length === 0) {
			const stringTools = tools as string[];
			return (
				<div className={`${className}`.trim()}>
					<div className="d-flex justify-content-end mb-2">
						<button
							type="button"
							className="btn btn-sm btn-outline-success"
							onClick={() => onChange([...stringTools, ''])}
						>
							+ Add
						</button>
					</div>
					<ul className="list-unstyled mb-0">
						{stringTools.map((tool, idx) => (
							<li key={idx} className="mb-2 d-flex align-items-center gap-2">
								<input
									type="text"
									className="form-control form-control-sm"
									value={tool}
									onChange={(e) => {
										const next = [...stringTools];
										next[idx] = e.target.value;
										onChange(next);
									}}
								/>
								<button
									type="button"
									className="btn btn-sm btn-outline-danger p-1"
									onClick={() => onChange(stringTools.filter((_, i) => i !== idx))}
								>
									<i className="bi bi-trash text-danger" aria-hidden />
								</button>
							</li>
						))}
					</ul>
				</div>
			);
		}

		const objectTools = tools as Array<{ name: string; description?: string }>;
		return (
			<div className={`${className}`.trim()}>
				<div className="d-flex justify-content-end mb-2">
					<button
						type="button"
						className="btn btn-sm btn-outline-success"
						onClick={() => onChange([...objectTools, { name: '', description: '' }])}
					>
						+ Add
					</button>
				</div>
				{objectTools.map((tool, idx) => (
					<div key={idx} className="mb-2 p-2 border rounded">
						<div className="d-flex align-items-center gap-2 mb-2">
							<input
								type="text"
								className="form-control form-control-sm"
								placeholder="Tool name"
								value={tool.name}
								onChange={(e) => {
									const next = [...objectTools];
									next[idx] = { ...next[idx], name: e.target.value };
									onChange(next);
								}}
							/>
							<button
								type="button"
								className="btn btn-sm btn-outline-danger p-1"
								onClick={() => onChange(objectTools.filter((_, i) => i !== idx))}
							>
								<i className="bi bi-trash text-danger" aria-hidden />
							</button>
						</div>
						<input
							type="text"
							className="form-control form-control-sm"
							placeholder="Optional description"
							value={tool.description ?? ''}
							onChange={(e) => {
								const next = [...objectTools];
								next[idx] = { ...next[idx], description: e.target.value };
								onChange(next);
							}}
						/>
					</div>
				))}
			</div>
		);
	}

	if (!tools.length) return null;

	if (isStringList) {
		return (
			<ul className={`list-unstyled mb-0 small ${className}`.trim()}>
				{(tools as string[]).map((tool, idx) => (
					<li key={idx} className="mb-2">
						<i className="bi bi-check-circle text-success me-2" style={{ fontSize: '0.75em' }} aria-hidden />
						<span style={{ fontSize: '0.9em' }}>{tool}</span>
					</li>
				))}
			</ul>
		);
	}

	return (
		<div className={`small ${className}`.trim()}>
			{(tools as Array<{ name: string; description?: string }>).map((tool, idx) => (
				<div key={idx} className="mb-3 pb-3 border-bottom border-light">
					<div className="fw-semibold mb-1" style={{ fontSize: '0.95em', color: '#495057' }}>
						<i className="bi bi-check-circle text-success me-2" style={{ fontSize: '0.85em' }} aria-hidden />
						{tool.name}
					</div>
					{tool.description ? (
						<div className="text-muted ms-4" style={{ fontSize: '0.85em' }}>
							{tool.description}
						</div>
					) : null}
				</div>
			))}
		</div>
	);
}
